import React, { useContext } from 'react';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import { Divider, Typography, Price, Adaptive, SizeableText, PriceProps } from 'fronton-react';
import get from 'lodash/get';
import { DataContext } from 'entries/common/widgets/context';
import {
  FALLBACK_PRODUCT_IMAGE_URL,
  SMALL_FALLBACK_PRODUCT_IMAGE_URL,
} from './fallback-product-images.const';

const AdaptiveImg = ({
  size: { width, height },
  fallback,
}: {
  size: { width: number; height: number };
  fallback: string;
}) => {
  const data = useContext(DataContext);
  const onError = ({ currentTarget }: { currentTarget: EventTarget & HTMLImageElement }) => {
    currentTarget.onerror = null; // prevents looping
    currentTarget.src = fallback;
  };

  return <img width={width} height={height} alt="product" src={data?.photo || fallback} onError={onError} />;
};

const AdaptiveTypography = ({ variant }: { variant: SizeableText }) => {
  const data = useContext(DataContext);
  return (
    <Typography variant={variant} size="body_short" color="text-primary">
      {get(data, 'displayedName', '')}
    </Typography>
  );
};

const AdaptivePrice = ({ size }: { size: PriceProps['size'] }) => {
  const data = useContext(DataContext);
  return (
    <Price
      variant="medium"
      size={size}
      fraction
      value={get(data, 'mainPrice', 0)}
      units={get(data, 'mainUomRus', '') ? `₽/${get(data, 'mainUomRus', '')}` : ''}
    />
  );
};

export const Product = () => {
  const data = useContext(DataContext);
  return (
    <ProductContainer>
      <ProductContent>
        <Adaptive to="tablet">
          <AdaptiveImg size={{ width: 48, height: 48 }} fallback={SMALL_FALLBACK_PRODUCT_IMAGE_URL} />
        </Adaptive>
        <Adaptive from="tablet">
          <AdaptiveImg size={{ width: 78, height: 78 }} fallback={FALLBACK_PRODUCT_IMAGE_URL} />
        </Adaptive>

        <ProductInfoContainer>
          <ProductInfoContent>
            <Typography variant="caption" color="text-minor">
              Арт. {get(data, 'productId', '00000000')}
            </Typography>
            <Adaptive to="tablet">
              <AdaptiveTypography variant="s" />
            </Adaptive>
            <Adaptive from="tablet">
              <AdaptiveTypography variant="m" />
            </Adaptive>
          </ProductInfoContent>
          <PriceContent>
            <Adaptive to="tablet">
              <AdaptivePrice size="s" />
            </Adaptive>
            <Adaptive from="tablet">
              <AdaptivePrice size="xl" />
            </Adaptive>
            <Price
              size="xs"
              fraction
              value={get(data, 'additionalPrice', 0)}
              units={get(data, 'additionalUomRus', '') ? `₽/${get(data, 'additionalUomRus', '')}` : ''}
              color="text-minor"
              align="right"
            />
          </PriceContent>
        </ProductInfoContainer>
      </ProductContent>
      <Divider className={dividerClassname} />
    </ProductContainer>
  );
};

const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductContent = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  @media (max-width: 767px) {
    margin-bottom: 16px;
  }
`;

const ProductInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: 14px 0 14px 16px;
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 8px;
    margin: 14px 0 0 16px;
  }
`;

const ProductInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media (max-width: 767px) {
    gap: 4px;
  }
`;

const PriceContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  white-space: nowrap;
  @media (max-width: 767px) {
    flex-direction: row;
  }
`;

const dividerClassname = css`
  margin: 0;
`;
